
// @ is an alias to /src
import { defineComponent, ref } from "vue";
import AppFooter from "@/components/common/AppFooter.vue";
import ConfirmationSave from "@/components/ConfirmationSave.vue";
import {
  required,
  helpers,
  requiredIf,
  minLength,
} from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import messages from "@/config/messages";
import numOnly from "@/utils/validations/numOnly";
import alphaNum from "@/utils/validations/alphaNum";
import { mapMutations, mapGetters } from "vuex";
import regexExps from "@/config/regexExps";
import helper from "@/utils/helper";
import common from "@/config/common";
import routesPath from "@/config/routesPath";
import { iSummaryData } from "@/types/iBenefitsData";
import eventBus from "@/utils/eventBus";
import ConfirmationTable from "@/components/ConfirmationTable.vue";
import { CARD_CHECKED, CARD_MP_CHECKED } from "@/utils/constants";
import * as apiClient from "@/apiclient/";
import benefitMapper from "@/utils/benefitMapper";
import iCOBRABenefit from "@/types/iCOBRABenefit";

export default defineComponent({
  name: "DependentsView",
  components: {
    AppFooter,
    ConfirmationSave,
    ConfirmationTable,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      payment: {
        amountDueToday: {
          routingNumber: "",
          bankName: "",
          accountNumber: "",
          verifyAccountNumber: "",
          accountHolderName: "",
          accountType: "",
        },
        monthlyPremium: {
          ach: {
            routingNumber: "",
            bankName: "",
            accountNumber: "",
            verifyAccountNumber: "",
            accountHolderName: "",
            accountType: "",
          },
          invoice: {
            countryId: 0 as number | null,
            addressLine1: "",
            city: "",
            addressLine2: "",
            zipCode: "",
            geoStateId: 0 as number | null,
          },
        },
      },
      isFormInvalid: false,
      validationMsg: "Oops, Something went wrong.",
      convenienceFee: 0,
      isSubmitDisabled: false,
      automaticCheckboxChecked: false,
      invoiceCheckboxChecked: true,
      cardMPChecked: false,
      achChecked: false,
      checkChecked: true,
      cardChecked: false,
      getFederalData: [] as apiClient.FederalAchDto[],
      hideAddressFields: false,
      allCountries: [] as apiClient.CountryResponseDto[],
      allStates: [] as apiClient.StateResponseDto[],
      allStatesOptions: [] as apiClient.StateResponseDto[],
      getSummary: [] as apiClient.BenefitSummaryDto[],
      arrSummary: {} as iSummaryData,
      benefitId: 0,
      copyAboveAddressCheckbox: false,
      messages,
      sameAsProfile: false,
      disableAddress: false,
      disableBankDetails: false,
      dataLoaded: false,
      common,
      countries: {} as any,
      payments: {
        amountDueToday: "amountDueToday",
        monthlyPremium: "monthlyPremium",
      },
      paymentMethods: {} as any,
      paymentType: { ach: 1, invoice: 2, card: 3 },
      accountTypes: {} as any,
      confirmationAlldata: [] as apiClient.ParticipantDetailDto,
      isStripeEnabled: false,
      profileData: {} as apiClient.ProfileResponseDto,
      qualifyingBeneficiaryData: {} as apiClient.QualifyingBeneficiaryDto,
      dependentData: [] as apiClient.GetAllDependentDto[],
      documentsData: [] as apiClient.FileResponse[],
      amountTodayData: {} as apiClient.GetAmountDueTodayDto,
      monthlyPremiumData: {} as apiClient.GetMonthlyPremiumDto,
      benefitsData: [] as iCOBRABenefit[],
      getCountryName: "",
      getStateName: "",
      getMonthlyCountryName: "",
      getMonthlyStateName: "",
      genderOptions: [] as apiClient.GenderDto[],
      showConfirmationModal: false,
      cardDetail: Object as apiClient.CardDetail,
    };
  },
  computed: {
    ...mapGetters(["getFormDirty", "getEnrollment"]),
    paymentValidations() {
      if (this.amountDuePayMethod() === common.paymentMethods.ACH) {
        // Define validation rules for amountDueToday
        return {
          routingNumber: {
            required,
            numOnly,
            minLength: minLength(9),
            validateRoutingNumber: (value: string) => {
              const bankDetails = this.getFederalData.find(
                (data: any) => data.routingNumber === value
              );
              return bankDetails ? true : false;
            },
          },
          bankName: { required },
          accountNumber: { required, alphaNum },
          verifyAccountNumber: {
            required,
            alphaNum,
            matchesAccountNumber: (value: any) => {
              return value === this.payment.amountDueToday.accountNumber;
            },
          },
          accountHolderName: { required },
          accountType: { required },
        };
      } else {
        return {};
      }
    },
  },
  validations() {
    return {
      payment: {
        amountDueToday: this.paymentValidations,
        monthlyPremium: {
          ach: {
            routingNumber: {
              required,
              numOnly,
              minLength: minLength(9),
              validateRoutingNumber: (value: string) => {
                const bankDetails = this.getFederalData.find(
                  (data: any) => data.routingNumber === value
                );
                return bankDetails ? true : false;
              },
            },
            bankName: { required },
            accountNumber: { required, alphaNum },
            verifyAccountNumber: {
              required,
              alphaNum,
              matchesAccountNumber: (value: any) => {
                return value === this.payment.monthlyPremium.ach.accountNumber;
              },
            },
            accountHolderName: { required },
            accountType: { required },
          },
          invoice: {
            countryId: { required },
            addressLine1: { required },
            city: {
              requiredIfCountryNotOther: requiredIf(this.isCountryNotOther),
            },
            zipCode: {
              requiredIfCountryNotOther: requiredIf(this.isCountryNotOther),
              validZip: helpers.withParams(
                { type: "validZip" },
                (value: string) => {
                  let regExp = "";
                  if (
                    this.payment.monthlyPremium.invoice.countryId ===
                    this.countries.USA
                  ) {
                    regExp = regexExps.zipcodeUS;
                  } else if (
                    this.payment.monthlyPremium.invoice.countryId ===
                    this.countries.CAN
                  ) {
                    regExp = regexExps.zipcodeCanada;
                  }
                  const regexValue = new RegExp(regExp);

                  return regexValue.test(value);
                }
              ),
            },
            geoStateId: {
              requiredIfCountryNotOther: requiredIf(this.isCountryNotOther),
            },
          },
        },
      },
    };
  },
  async created() {
    eventBus.emit("toggle-enrollments-menu", false);
    await this.getStripeEnabled();
    await this.getPaymentMethods();
    await this.getAccountTypes();
    await this.getLocations();
    await this.getBenefitSummary();
    await this.getFederalAchData();

    for (let field in this.payment.amountDueToday) {
      this.$watch(
        () => this.v$.payment.amountDueToday[field]?.$dirty,
        (newDirty) => {
          if (newDirty) {
            this.setFormDirty(true);
          }
        }
      );
    }
    for (let field in this.payment.monthlyPremium.ach) {
      this.$watch(
        () => this.v$.payment.monthlyPremium.ach[field]?.$dirty,
        (newDirty) => {
          if (newDirty) {
            this.setFormDirty(true);
          }
        }
      );
    }
    for (let field in this.payment.monthlyPremium.invoice) {
      this.$watch(
        () => this.v$.payment.monthlyPremium.invoice[field]?.$dirty,
        (newDirty) => {
          if (newDirty) {
            this.setFormDirty(true);
          }
        }
      );
    }
    window.addEventListener("beforeunload", this.confirmBeforeUnload);
  },
  beforeUnmount() {
    // Remove the beforeunload event handler when the component is destroyed
    window.removeEventListener("beforeunload", this.confirmBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    helper.beforeRouterLeave(next);
  },
  methods: {
    ...mapMutations([
      "setBenefitRates",
      "setFormDirty",
      "setWasSpecialOpenEnrollmentCreated",
    ]),
    changeCountry() {
      this.v$.payment.monthlyPremium.invoice.countryId.$touch();
      this.getStateOptions();
    },
    isCountryNotOther() {
      return (
        this.payment.monthlyPremium.invoice.countryId !== this.countries.OTH
      );
    },
    updateMonthlyBankDetails(field = "") {
      if (field === "accountType") {
        this.v$.payment.amountDueToday.accountType.$touch();
      }
      if (
        !this.v$.payment.amountDueToday.$invalid &&
        this.copyAboveAddressCheckbox
      ) {
        this.copyDueTodayToMonthlyAutomatic();
      }
    },
    confirmBeforeUnload(event: any) {
      helper.confirmBeforeUnload(event);
    },
    isValidForm() {
      if (!this.dataLoaded) return false;

      if (
        (!this.achChecked && !this.checkChecked && !this.cardChecked) ||
        (!this.invoiceCheckboxChecked &&
          !this.automaticCheckboxChecked &&
          !this.cardMPChecked)
      ) {
        return true;
      }
      if (
        this.v$.payment.amountDueToday.$invalid ||
        (this.monthlyPremiumMethod() === common.paymentMethods.ACH &&
          this.v$.payment.monthlyPremium.ach.$invalid) ||
        (this.monthlyPremiumMethod() === common.paymentMethods.Invoice &&
          this.v$.payment.monthlyPremium.invoice.$invalid)
      ) {
        return true;
      }

      return false;
    },
    copyDueTodayToMonthlyAutomatic() {
      this.setFormDirty(true);
      if (this.copyAboveAddressCheckbox) {
        this.payment.monthlyPremium.ach.routingNumber =
          this.payment.amountDueToday.routingNumber;
        this.payment.monthlyPremium.ach.bankName =
          this.payment.amountDueToday.bankName;
        this.payment.monthlyPremium.ach.accountNumber =
          this.payment.amountDueToday.accountNumber;
        this.payment.monthlyPremium.ach.verifyAccountNumber =
          this.payment.amountDueToday.verifyAccountNumber;
        this.payment.monthlyPremium.ach.accountHolderName =
          this.payment.amountDueToday.accountHolderName;
        this.payment.monthlyPremium.ach.accountType =
          this.payment.amountDueToday.accountType;
        this.disableBankDetails = true;
      } else {
        this.payment.monthlyPremium.ach.routingNumber = "";
        this.payment.monthlyPremium.ach.bankName = "";
        this.payment.monthlyPremium.ach.accountNumber = "";
        this.payment.monthlyPremium.ach.verifyAccountNumber = "";
        this.payment.monthlyPremium.ach.accountHolderName = "";
        this.payment.monthlyPremium.ach.accountType = "";
        this.disableBankDetails = false;
        this.v$.payment.monthlyPremium.ach.$touch();
      }
    },
    async getBenefitSummary(): Promise<void> {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient.paymentController().getApiPayment(params);
      if (!result || result.error) {
        this.dataLoaded = true;

        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.dataLoaded = true;
        this.convenienceFee = result.data.convenienceFee!;
        this.getSummary = result.data.benefitSummary!;
        helper.setBenefitSummary(result.data.benefitSummary!);
        this.setMonthlyPremiumData(
          result.data.getMonthlyPremium!,
          result.data.getAmountDueToday!
        );
        this.setAmountDueData(result.data.getAmountDueToday!);
      }
    },
    setAmountDueData(amountDueData: any) {
      if (!amountDueData) {
        return;
      }
      if (amountDueData.payMethod === common.paymentMethods.ACH) {
        this.achChecked = true;
        this.checkChecked = false;
        this.cardChecked = false;
        eventBus.emit(CARD_CHECKED, false);
        this.payment.amountDueToday.routingNumber = amountDueData.routingNumber;
        this.payment.amountDueToday.bankName = amountDueData.bankName;
        this.payment.amountDueToday.accountNumber = amountDueData.accountNumber;
        this.payment.amountDueToday.verifyAccountNumber =
          amountDueData.accountNumber;
        this.payment.amountDueToday.accountHolderName =
          amountDueData.nameOnAccount;
        this.payment.amountDueToday.accountType = amountDueData.accountType;
      } else if (amountDueData.payMethod === common.paymentMethods.Invoice) {
        this.checkChecked = true;
        this.achChecked = false;
        this.cardChecked = false;
        eventBus.emit(CARD_CHECKED, false);
      } else if (
        amountDueData.payMethod === common.paymentMethods.Card &&
        this.isStripeEnabled
      ) {
        this.cardChecked = true;
        eventBus.emit(CARD_CHECKED, true);
        this.checkChecked = false;
        this.achChecked = false;
      }
    },
    setMonthlyPremiumData(monthlyPremiumData: any, amountDueToday: any) {
      if (!monthlyPremiumData) {
        return;
      }
      if (monthlyPremiumData.payMethod === common.paymentMethods.Invoice) {
        this.automaticCheckboxChecked = false;
        this.invoiceCheckboxChecked = true;
        this.cardMPChecked = false;
        eventBus.emit(CARD_MP_CHECKED, false);
        this.payment.monthlyPremium.invoice.countryId =
          monthlyPremiumData.billingCountryId;
        this.payment.monthlyPremium.invoice.addressLine1 =
          monthlyPremiumData.billingAddress1;
        this.payment.monthlyPremium.invoice.addressLine2 =
          monthlyPremiumData.billingAddress2;
        this.payment.monthlyPremium.invoice.city =
          monthlyPremiumData.billingCity;
        this.payment.monthlyPremium.invoice.geoStateId =
          monthlyPremiumData.billingGeoStateId;
        this.payment.monthlyPremium.invoice.zipCode =
          monthlyPremiumData.billingZipCode;
        this.getStateOptions();
        this.sameAsProfile = monthlyPremiumData.paySameInfo;
        this.disableAddress = monthlyPremiumData.paySameInfo;
      } else if (monthlyPremiumData.payMethod === common.paymentMethods.ACH) {
        this.automaticCheckboxChecked = true;
        this.invoiceCheckboxChecked = false;
        this.cardMPChecked = false;
        eventBus.emit(CARD_MP_CHECKED, false);
        this.payment.monthlyPremium.ach.routingNumber =
          monthlyPremiumData.routingNumber;
        this.payment.monthlyPremium.ach.bankName = monthlyPremiumData.bankName;
        this.payment.monthlyPremium.ach.accountNumber =
          monthlyPremiumData.accountNumber;
        this.payment.monthlyPremium.ach.verifyAccountNumber =
          monthlyPremiumData.accountNumber;
        this.payment.monthlyPremium.ach.accountHolderName =
          monthlyPremiumData.nameOnAccount;
        this.payment.monthlyPremium.ach.accountType =
          monthlyPremiumData.accountType;
        this.copyAboveAddressCheckbox = monthlyPremiumData.paySameInfo;
        this.disableBankDetails = monthlyPremiumData.paySameInfo;
      } else if (
        monthlyPremiumData.payMethod === common.paymentMethods.Card &&
        this.isStripeEnabled
      ) {
        this.automaticCheckboxChecked = false;
        this.invoiceCheckboxChecked = false;
        this.cardMPChecked = true;
        eventBus.emit(CARD_MP_CHECKED, true);
      }
    },
    setSameAsBankDetails(amountDueToday: any) {
      if (!amountDueToday) return;
      if (
        amountDueToday.payMethod === common.paymentMethods.ACH &&
        this.payment.monthlyPremium.ach.routingNumber ===
          amountDueToday.routingNumber &&
        this.payment.monthlyPremium.ach.bankName === amountDueToday.bankName &&
        this.payment.monthlyPremium.ach.accountNumber ===
          amountDueToday.accountNumber &&
        this.payment.monthlyPremium.ach.verifyAccountNumber ===
          amountDueToday.accountNumber &&
        this.payment.monthlyPremium.ach.accountHolderName ===
          amountDueToday.nameOnAccount &&
        this.payment.monthlyPremium.ach.accountType ===
          amountDueToday.accountType
      ) {
        this.copyAboveAddressCheckbox = true;
        this.disableBankDetails = true;
      }
    },
    async setSameAsProfile() {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient.profileController().getApiProfile(params);
      if (result.data) {
        const profile = result.data;
        if (
          this.payment.monthlyPremium.invoice.countryId === profile.countryId &&
          this.payment.monthlyPremium.invoice.addressLine1 ===
            profile.address1 &&
          this.payment.monthlyPremium.invoice.addressLine2 ===
            profile.address2 &&
          this.payment.monthlyPremium.invoice.city === profile.city &&
          this.payment.monthlyPremium.invoice.geoStateId ===
            profile.geoStateId &&
          this.payment.monthlyPremium.invoice.zipCode === profile.zipCode
        ) {
          this.sameAsProfile = true;
          this.disableAddress = true;
        }
      }
    },
    subHeading(amountType: string) {
      if (amountType === this.payments.amountDueToday) {
        if (this.isStripeEnabled) {
          return 'Please select a method of payment. If you select "Card", you will be asked to enter the information on the following page.';
        } else {
          return "This is the amount due today. Please choose to pay by check or automatic withdrawal to have the payment deducted from your designated bank account. You will not be enrolled in coverage until payment is received.";
        }
      } else if (amountType === this.payments.monthlyPremium) {
        if (this.isStripeEnabled) {
          return 'If you choose "Invoice" or "Card", you will receive a monthly invoice. If you choose "Automatic (ACH)", you will not receive a monthly invoice.';
        } else {
          return "Choose to receive an invoice for future monthly payments or set up automatic withdrawal to have the payment deducted from your designated bank account. If you select automatic withdrawal, you can choose the same bank account you used to complete the payment owed today, or you can provide new banking information.";
        }
      }
    },
    totalPayment(amountType: string, cardChecked: boolean) {
      let total = 0;
      if (amountType === this.payments.amountDueToday) {
        this.getSummary.forEach((summary: apiClient.BenefitSummaryDto) => {
          total += summary.initialAmount!;
        });
      } else if (amountType === this.payments.monthlyPremium) {
        this.getSummary.forEach((summary: apiClient.BenefitSummaryDto) => {
          total += summary.monthlyPremium!;
        });
      }
      total = cardChecked ? total + this.convenienceFee : total;
      return helper.formatNumber(total);
    },
    amountDuePayMethod() {
      if (this.achChecked) {
        return common.paymentMethods.ACH;
      } else if (this.checkChecked) {
        return common.paymentMethods.Invoice;
      } else if (this.cardChecked) {
        return common.paymentMethods.Card;
      }
    },
    monthlyPremiumMethod() {
      if (this.automaticCheckboxChecked) {
        return common.paymentMethods.ACH;
      } else if (this.invoiceCheckboxChecked) {
        return common.paymentMethods.Invoice;
      } else if (this.cardMPChecked) {
        return common.paymentMethods.Card;
      }
    },
    getStateOptions() {
      this.hideAddressFields = false;
      if (
        this.payment.monthlyPremium.invoice.countryId === this.countries.OTH
      ) {
        this.hideAddressFields = true;
      }
      this.allStatesOptions = this.allStates.filter((state) => {
        return (
          this.payment.monthlyPremium.invoice.countryId === state.countryId
        );
      });
    },
    async getAccountTypes(): Promise<void> {
      const result = await apiClient
        .paymentController()
        .getApiPaymentAccountType();
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.accountTypes = result.data;
      }
    },
    async getPaymentMethods() {
      const result = await apiClient.paymentController().getApiPaymentType();
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.paymentMethods = result.data;
      }
    },
    async getLocations() {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .profileController()
        .getApiProfilePrimary(params);
      if (result.data) {
        const profile = result.data;
        if (this.payment.monthlyPremium.invoice) {
          const location = await helper.getLocationDetails(
            profile.countryId!,
            profile.geoStateId!,
            this.payment.monthlyPremium.invoice.countryId as number,
            this.payment.monthlyPremium.invoice.geoStateId as number
          );
          this.getCountryName = location?.getCountryName;
          this.getMonthlyCountryName = location?.getMonthlyCountryName;
          this.getStateName = location?.getStateName;
          this.getMonthlyStateName = location?.getMonthlyStateName;
        }
      }
      const { allCountries, allStates, countries } =
        await helper.getLocations();

      this.allCountries = allCountries;
      this.allStates = allStates;
      this.countries = countries;
    },
    setBankName(achType: string, selectedRoutingNumber: string) {
      const bank = this.getFederalData.find(
        (data: any) => data.routingNumber === selectedRoutingNumber
      );
      if (achType === this.payments.amountDueToday) {
        this.payment.amountDueToday.bankName = bank! ? bank.bankName! : "";
      } else {
        this.payment.monthlyPremium.ach.bankName = bank! ? bank.bankName! : "";
      }
    },
    async getFederalAch(achType: string): Promise<void> {
      let selectedRoutingNumber = "";
      if (achType === this.payments.amountDueToday) {
        this.v$.payment.amountDueToday.routingNumber.$touch();
        selectedRoutingNumber = this.payment.amountDueToday.routingNumber;
      } else {
        this.v$.payment.monthlyPremium.ach.routingNumber.$touch();
        selectedRoutingNumber = this.payment.monthlyPremium.ach.routingNumber;
      }
      if (this.getFederalData.length) {
        this.setBankName(achType, selectedRoutingNumber);
        return;
      }
      if (!selectedRoutingNumber) {
        return;
      }
    },
    async getFederalMonthlyAch(): Promise<void> {
      const result = await apiClient
        .paymentController()
        .getApiPaymentFederalAch();
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
          this.getFederalData = [];
        }
        return;
      } else if (result.data) {
        this.getFederalData = result.data;
        const selectedRoutingNumberMonthly =
          this.payment.monthlyPremium.ach.routingNumber;
        if (!selectedRoutingNumberMonthly) {
          return;
        } else {
          const bankNameMonthly = this.getFederalData.find(
            (data: any) => data.routingNumber === selectedRoutingNumberMonthly
          );
          this.payment.monthlyPremium.ach.bankName = bankNameMonthly!
            ? bankNameMonthly.bankName!
            : "";
        }
      }
    },
    async savePaymentDetails(): Promise<boolean> {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
        amountDueToday: {
          electionInitialAmountPayMethodId: 0,
          payMethod: this.amountDuePayMethod(),
          routingNumber: this.payment.amountDueToday.routingNumber,
          accountNumber: this.payment.amountDueToday.accountNumber,
          accountType: this.payment.amountDueToday.accountType,
          nameOnAccount: this.payment.amountDueToday.accountHolderName,
        },
        monthlyPremium: {
          payMethod: this.monthlyPremiumMethod(),
          paySameInfo:
            this.monthlyPremiumMethod() === common.paymentMethods.Invoice
              ? this.sameAsProfile
              : this.copyAboveAddressCheckbox,
          routingNumber: this.payment.monthlyPremium.ach.routingNumber,
          accountNumber: this.payment.monthlyPremium.ach.accountNumber,
          accountType: this.payment.monthlyPremium.ach.accountType,
          nameOnAccount: this.payment.monthlyPremium.ach.accountHolderName,
          billingCountryId: this.payment.monthlyPremium.invoice.countryId,
          billingAddress1: this.payment.monthlyPremium.invoice.addressLine1,
          billingAddress2: this.payment.monthlyPremium.invoice.addressLine2,
          billingCity: this.payment.monthlyPremium.invoice.city,
          billingGeoStateId: this.payment.monthlyPremium.invoice.geoStateId,
          billingZipCode: this.payment.monthlyPremium.invoice.zipCode,
        },
      };
      this.isSubmitDisabled = true;
      const result = await apiClient.paymentController().postApiPayment(params);
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        this.isSubmitDisabled = false;
        this.setFormDirty(false);
        return false;
      } else if (result.data) {
        helper.setPageStep(common.pageStep.payments);
        this.isFormInvalid = false;
        this.setFormDirty(false);

        return true;
      }
      return false;
    },
    async submitHandler(): Promise<void> {
      if (
        this.isStripeEnabled &&
        (this.amountDuePayMethod() === common.paymentMethods.Card ||
          this.monthlyPremiumMethod() === common.paymentMethods.Card)
      ) {
        const result = await this.savePaymentDetails();
        if (result) {
          sessionStorage.setItem(common.storageVariables.paymentMethod, "Card");
          this.isSubmitDisabled = false;
          eventBus.emit("toggle-stripe-menu", true);
          this.$router.push({ name: routesPath.cardDetails });
        }
      } else {
        sessionStorage.removeItem(common.storageVariables.paymentMethod);
        eventBus.emit("toggle-stripe-menu", false);
        this.openConfirmationModal();
      }
    },
    async confirmEnrollment() {
      this.closeConfirmationModal();
      const result = await this.savePaymentDetails();
      if (result) {
        await this.getGender();
        await this.getPaymentMethods();
        await this.getAccountTypes();
        await this.getAllData();
        await this.getLocations();
        document.getElementById("pdfContentContainer")!.style.display = "block";
        const pdfBlob: Blob = await helper.printPDF(
          document.getElementById("pdfContent")!,
          false,
          true
        );
        document.getElementById("pdfContentContainer")!.style.display = "none";
        this.afterPdfCreate(pdfBlob);
      }
    },
    openConfirmationModal() {
      this.showConfirmationModal = true;
    },
    closeConfirmationModal() {
      this.showConfirmationModal = false;
    },
    async getAllData(): Promise<void> {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .confirmationController()
        .getApiConfirmation(params);
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.confirmationAlldata = result.data;
        this.profileData = result.data.profile!;
        this.qualifyingBeneficiaryData = result.data.qualifyingBeneficiary!;
        this.dependentData = result.data.dependents!;
        this.documentsData = result.data.documents!;
        this.amountTodayData = result.data.getPaymentAmountDueToday!;
        this.monthlyPremiumData = result.data.getPaymentMonthlyPremium!;
        this.benefitsData = benefitMapper.mapCOBRAApiBenefitsToBenefits(
          result.data.benefits!
        );
        this.cardDetail = result.data.cardDetail!;
      }
    },
    async getGender(): Promise<void> {
      let genderData = await helper.getGenders();
      if (genderData.validationMsg.length > 0) {
        this.validationMsg = genderData.validationMsg;
      } else {
        this.genderOptions = genderData.genderOptions!;
      }
    },
    async afterPdfCreate(pdfBlob: Blob) {
      const params = {
        File: pdfBlob,
        customerParticipantId:
          this.getEnrollment.customerParticipantId.toString(),
      };
      const result = await apiClient
        .confirmationController()
        .putApiConfirmation(params);
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.setFormDirty(false);
        sessionStorage.setItem(
          common.storageVariables.applicationSubmitted,
          "yes"
        );
        sessionStorage.setItem(common.storageVariables.isSubmitted, "yes");
        this.setWasSpecialOpenEnrollmentCreated(
          result.data.specialOpenEnrollmentCreated
        );
        this.$router.push({ name: routesPath.applicationSuccessful });
      }
    },
    handleAutomaticCheckboxClick() {
      this.automaticCheckboxChecked = true;
      this.invoiceCheckboxChecked = false;
      this.cardMPChecked = false;
      eventBus.emit(CARD_MP_CHECKED, false);
      this.setFormDirty(true);
      this.sameAsProfile = false;
      this.disableAddress = false;
      this.payment.monthlyPremium.invoice.countryId = null;
      this.payment.monthlyPremium.invoice.addressLine1 = "";
      this.payment.monthlyPremium.invoice.addressLine2 = "";
      this.payment.monthlyPremium.invoice.city = "";
      this.payment.monthlyPremium.invoice.geoStateId = null;
      this.payment.monthlyPremium.invoice.zipCode = "";
    },
    handleInvoiceCheckboxClick() {
      this.invoiceCheckboxChecked = true;
      this.automaticCheckboxChecked = false;
      this.cardMPChecked = false;
      eventBus.emit(CARD_MP_CHECKED, false);
      this.setFormDirty(true);
      this.copyAboveAddressCheckbox = false;
      this.disableBankDetails = false;
      this.payment.monthlyPremium.ach.routingNumber = "";
      this.payment.monthlyPremium.ach.bankName = "";
      this.payment.monthlyPremium.ach.accountNumber = "";
      this.payment.monthlyPremium.ach.verifyAccountNumber = "";
      this.payment.monthlyPremium.ach.accountHolderName = "";
      this.payment.monthlyPremium.ach.accountType = "";
    },
    handleCardMPCheckboxClick() {
      this.cardMPChecked = true;
      eventBus.emit(CARD_MP_CHECKED, true);
      this.invoiceCheckboxChecked = false;
      this.automaticCheckboxChecked = false;
      this.setFormDirty(true);
      this.copyAboveAddressCheckbox = false;
      this.disableBankDetails = false;
      this.payment.monthlyPremium.ach.routingNumber = "";
      this.payment.monthlyPremium.ach.bankName = "";
      this.payment.monthlyPremium.ach.accountNumber = "";
      this.payment.monthlyPremium.ach.verifyAccountNumber = "";
      this.payment.monthlyPremium.ach.accountHolderName = "";
      this.payment.monthlyPremium.ach.accountType = "";
      this.sameAsProfile = false;
      this.disableAddress = false;
      this.payment.monthlyPremium.invoice.countryId = null;
      this.payment.monthlyPremium.invoice.addressLine1 = "";
      this.payment.monthlyPremium.invoice.addressLine2 = "";
      this.payment.monthlyPremium.invoice.city = "";
      this.payment.monthlyPremium.invoice.geoStateId = null;
      this.payment.monthlyPremium.invoice.zipCode = "";
    },
    handleAmountCheckboxClick() {
      this.achChecked = true;
      this.checkChecked = false;
      this.cardChecked = false;
      eventBus.emit(CARD_CHECKED, false);
      this.setFormDirty(true);
    },
    handleCheckaAmountCheckboxClick() {
      this.checkChecked = true;
      this.achChecked = false;
      this.cardChecked = false;
      eventBus.emit(CARD_CHECKED, false);
      this.setFormDirty(true);
      this.copyAboveAddressCheckbox = false;
      this.disableBankDetails = false;
      this.payment.amountDueToday.routingNumber = "";
      this.payment.amountDueToday.bankName = "";
      this.payment.amountDueToday.accountNumber = "";
      this.payment.amountDueToday.verifyAccountNumber = "";
      this.payment.amountDueToday.accountHolderName = "";
      this.payment.amountDueToday.accountType = "";
    },
    handleCardCheckboxClick() {
      this.checkChecked = false;
      eventBus.emit(CARD_CHECKED, true);
      this.achChecked = false;
      this.setFormDirty(true);
      this.copyAboveAddressCheckbox = false;
      this.disableBankDetails = false;
      this.payment.amountDueToday.routingNumber = "";
      this.payment.amountDueToday.bankName = "";
      this.payment.amountDueToday.accountNumber = "";
      this.payment.amountDueToday.verifyAccountNumber = "";
      this.payment.amountDueToday.accountHolderName = "";
      this.payment.amountDueToday.accountType = "";
    },
    async updateAddress() {
      this.setFormDirty(true);
      if (this.sameAsProfile) {
        const params = {
          customerParticipantId: this.getEnrollment.customerParticipantId,
        };
        const result = await apiClient
          .profileController()
          .getApiProfile(params);
        if (!result || result.error) {
          this.isFormInvalid = true;
          if (result.error) {
            this.validationMsg = result.error + messages.errorMsg;
          }
          this.disableAddress = false;
          return;
        } else if (result.data) {
          const profile = result.data;

          this.payment.monthlyPremium.invoice.countryId = profile.countryId!;
          this.payment.monthlyPremium.invoice.addressLine1 = profile.address1!;
          this.payment.monthlyPremium.invoice.addressLine2 = profile.address2!;
          this.payment.monthlyPremium.invoice.city = profile.city!;
          this.payment.monthlyPremium.invoice.geoStateId = profile.geoStateId!;
          this.payment.monthlyPremium.invoice.zipCode = profile.zipCode!;
          this.getStateOptions();
          this.disableAddress = true;
        }
      } else {
        this.payment.monthlyPremium.invoice.countryId = null;
        this.payment.monthlyPremium.invoice.addressLine1 = "";
        this.payment.monthlyPremium.invoice.addressLine2 = "";
        this.payment.monthlyPremium.invoice.city = "";
        this.payment.monthlyPremium.invoice.geoStateId = null;
        this.payment.monthlyPremium.invoice.zipCode = "";
        this.disableAddress = false;
        this.v$.payment.monthlyPremium.invoice.$touch();
      }
    },
    async getFederalAchData() {
      const result = await apiClient
        .paymentController()
        .getApiPaymentFederalAch();
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
          this.getFederalData = [];
        }
        return;
      } else if (result.data) {
        this.getFederalData = result.data;
      }
    },
    async getStripeEnabled() {
      let stripeData = await helper.getStripeEnabled();
      if (stripeData) {
        if (stripeData!.validationMsg.length > 0) {
          this.validationMsg = stripeData.validationMsg;
          this.isStripeEnabled = false;
        } else {
          this.isStripeEnabled = stripeData.isStripeEnabled!;
        }
      }
    },
  },
});
